import { Cell, Row } from "react-table";
import { CustomFieldOption } from "src/api/contractGroup/contractgroup-type";
import { CUSTOM_FIELD_VISIBLE_MAX, PartnerMemberCustomFieldModel } from "src/api/userAdditionalInfo/userAdditionalInfo-type";
import { BaseButton, BaseToggle, BaseTooltip } from "src/components";
import { FIELD_TYPE_OPTIONS } from "../../userAdditionalInfo-constant";

// 노출/순서/삭제 토글 변경 시 페이지로 전달되는 editType
export type CustomFieldEditType = "orderNum" | "visible" | "appVisible" | "appEditable";

type UpdateFn = (request: PartnerMemberCustomFieldModel | PartnerMemberCustomFieldModel[], editType?: CustomFieldEditType) => Promise<void>;

export const customFieldColumns = [
  {
    Header: "순서",
    accessor: "",
    width: 80,
    Cell: ({ row, rows, onUpdateCustomField }: { row: Row<PartnerMemberCustomFieldModel>; rows: Row<PartnerMemberCustomFieldModel>[]; onUpdateCustomField: UpdateFn }) => {
      const handleOrderChange = (direction: -1 | 1) => {
        const currentItem = row.original; // 클릭한 현재 필드
        const targetItem = rows[row.index + direction].original; // 이동할 target 필드

        const updatedCurrentItem = {
          fieldId: currentItem.fieldId,
          orderNum: targetItem.orderNum, // 현재 필드에 대상 필드의 순번 부여
        };
        const updatedTargetItem = {
          fieldId: targetItem.fieldId,
          orderNum: currentItem.orderNum, // 대상 필드에 현재 필드의 순번 부여
        };

        onUpdateCustomField([updatedCurrentItem, updatedTargetItem], "orderNum");
      };

      return (
        <div className="flex-center " style={{ height: 30 }}>
          <BaseButton //
            title=""
            className={`base-btn ic-chevron-up mr2 cursor-pointer color-white pa0 h30 size-small`}
            disabled={row.index === 0}
            onClick={() => handleOrderChange(-1)}
          />
          <BaseButton //
            title=""
            className={`base-btn ic-chevron-down  cursor-pointer color-white pa0 h30`}
            disabled={row.index === rows.length - 1}
            onClick={() => handleOrderChange(1)}
          />
        </div>
      );
    },
  },
  {
    Header: "id",
    accessor: "fieldId",
    width: 60,
  },
  {
    // 물리명 (ESL 연동용 slug) - 값 입력/수정은 [설정] 모달에서 처리
    Header: "물리명",
    accessor: "slug",
    width: 160,
    Cell: ({ row }: Cell<PartnerMemberCustomFieldModel>) => <BaseTooltip contents={row.original.slug || "-"} isSingleLine={true} />,
  },
  {
    Header: "필드명",
    accessor: "fieldName",
    width: 180,
  },
  {
    Header: "필드유형",
    accessor: "fieldType",
    width: 120,
    Cell: ({ row }: Cell<PartnerMemberCustomFieldModel>) => {
      const fieldType = row.original.fieldType;
      return <BaseTooltip contents={FIELD_TYPE_OPTIONS.find((option) => option.value === fieldType)?.label} isSingleLine={true} />;
    },
  },
  {
    Header: "필드값(옵션)",
    accessor: "optionList",
    width: 240,
    Cell: ({ row }: Cell<PartnerMemberCustomFieldModel>) => {
      const options = row.original.optionList?.map((option: CustomFieldOption) => option.optionName).join(", ");
      return <BaseTooltip contents={options || "-"} isSingleLine={true} />;
    },
  },
  {
    // Ctrl.Room 노출 (최대 CUSTOM_FIELD_VISIBLE_MAX 개)
    Header: ({ rows }: { rows: Row<PartnerMemberCustomFieldModel>[] }) => {
      const visibleCount = rows.filter((row) => row.original.isVisible).length;
      return `Ctrl.Room 노출 (${visibleCount}/${CUSTOM_FIELD_VISIBLE_MAX})`;
    },
    accessor: "isVisible",
    width: 140,
    Cell: ({ row, onUpdateCustomField }: { row: Row<PartnerMemberCustomFieldModel>; onUpdateCustomField: UpdateFn }) => (
      <BaseToggle
        onChange={(checked: boolean) => {
          onUpdateCustomField({ ...row.original, isVisible: checked }, "visible");
        }}
        checked={row.original.isVisible}
      />
    ),
  },
  {
    // 사용앱 노출 (최대 CUSTOM_FIELD_VISIBLE_MAX 개, Ctrl.Room 노출 필드만 가능)
    Header: ({ rows }: { rows: Row<PartnerMemberCustomFieldModel>[] }) => {
      const appVisibleCount = rows.filter((row) => row.original.isAppVisible).length;
      return `사용앱 노출 (${appVisibleCount}/${CUSTOM_FIELD_VISIBLE_MAX})`;
    },
    accessor: "isAppVisible",
    width: 140,
    Cell: ({ row, onUpdateCustomField }: { row: Row<PartnerMemberCustomFieldModel>; onUpdateCustomField: UpdateFn }) => (
      <BaseToggle
        // isVisible=true 여야 사용앱 노출 가능 (eCT117 방지)
        disabled={!row.original.isVisible}
        onChange={(checked: boolean) => {
          onUpdateCustomField({ ...row.original, isAppVisible: checked }, "appVisible");
        }}
        checked={row.original.isAppVisible}
      />
    ),
  },
  {
    // 사용앱 수정 (사용앱 노출 필드만 가능)
    Header: "사용앱 수정",
    accessor: "isAppEditable",
    width: 100,
    Cell: ({ row, onUpdateCustomField }: { row: Row<PartnerMemberCustomFieldModel>; onUpdateCustomField: UpdateFn }) => (
      <BaseToggle
        // isAppVisible=true 여야 사용앱 수정 가능 (eCT117 방지)
        disabled={!row.original.isAppVisible}
        onChange={(checked: boolean) => {
          onUpdateCustomField({ ...row.original, isAppEditable: checked }, "appEditable");
        }}
        checked={row.original.isAppEditable}
      />
    ),
  },
  {
    Header: "설정",
    accessor: "",
    width: 80,
    Cell: ({ row, handleSetCustomFieldDetail }: { row: Row<PartnerMemberCustomFieldModel>; handleSetCustomFieldDetail: (detail: PartnerMemberCustomFieldModel) => void }) => (
      <BaseButton
        className="color-white"
        title="설정"
        onClick={() => {
          handleSetCustomFieldDetail(row.original);
        }}
      />
    ),
  },
  {
    Header: "삭제",
    accessor: "",
    width: 48,
    Cell: ({ row, onDeleteCustomField }: { row: Row<PartnerMemberCustomFieldModel>; onDeleteCustomField: (request: PartnerMemberCustomFieldModel) => Promise<void> }) => {
      return (
        <button
          className="base-trash-btn color-bg-gray h40"
          onClick={() => {
            onDeleteCustomField({ ...row.original, isDeleted: true });
          }}
        />
      );
    },
  },
];
